import React, { useState, useEffect } from "react";
import {
  FileText,
  Upload,
  Search,
  Filter,
  Download,
  Eye,
  Folder,
  Clock,
  BarChart3,
  TrendingUp,
  RefreshCw,
  AlertCircle,
  Calendar,
  Tag,
  User as UserIcon,
  Users,
} from "lucide-react";
import MasterSideBar from "../../components/MasterSideBar"; // Unified Sidebar
import Topbar from "../../components/TopbarNav";
import UploadDocumentModal from "../../components/UploadDocumentModal";
import { motion, AnimatePresence } from "framer-motion";
import { getDocuments } from "../../api/documentApi";

export default function MyUploads() {
  const [documents, setDocuments] = useState([]);
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Dynamic layout state
  const [staffId, setStaffId] = useState(null);
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    try {
      const rawUser = localStorage.getItem("user");
      const user = rawUser ? JSON.parse(rawUser) : null;
      if (user && user.id) {
        setStaffId(user.id);
      } else {
        setError("Staff profile not found.");
      }
    } catch (e) {
      setError("Failed to load user information.");
    }
  }, []);

  const fetchDocuments = async () => {
    if (!staffId) { setIsLoading(false); return; }
    setIsLoading(true);
    setError(null);
    try {
      const data = await getDocuments("Staff", staffId);
      if (!Array.isArray(data)) throw new Error("Invalid data format");
      const documentsWithFlags = data.map(doc => ({
        ...doc,
        isOwned: true,
        isAssigned: false,
        isShared: false,
      }));
      setDocuments(documentsWithFlags);
    } catch (err) {
      setError(`Failed to load documents: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [staffId]);

  const handleRefresh = () => fetchDocuments();

  const handleUploadSuccess = async () => {
    await fetchDocuments();
    setIsModalOpen(false);
  };

  const getFilteredDocuments = () => {
    let filtered = documents.filter((doc) => {
      const matchesCategory = filterCategory === "All" || doc.category === filterCategory;
      const matchesType = filterType === "All" || (filterType === "owned" && doc.isOwned);
      const matchesSearch = doc.originalName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           doc.category?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesType && matchesSearch;
    });

    filtered.sort((a, b) => {
      let aVal, bVal;
      if (sortBy === "name") {
        aVal = a.originalName?.toLowerCase() || "";
        bVal = b.originalName?.toLowerCase() || "";
      } else {
        aVal = new Date(a.createdAt);
        bVal = new Date(b.createdAt);
      }
      return sortOrder === "asc" ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
    return filtered;
  };

  const filteredDocuments = getFilteredDocuments();
  const categoryStats = documents.reduce((acc, doc) => {
    const cat = doc.category || "Uncategorized";
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});

  const categories = [
    { name: "All", icon: Folder, count: documents.length, color: "bg-orange-600", description: "All documents" },
    { name: "Salary Documents", icon: BarChart3, count: categoryStats["Salary Documents"] || 0, color: "bg-blue-600", description: "Payroll" },
    { name: "Agreements & Contracts", icon: FileText, count: categoryStats["Agreements & Contracts"] || 0, color: "bg-green-600", description: "Contracts" },
    { name: "Audit Reports", icon: TrendingUp, count: categoryStats["Audit Reports"] || 0, color: "bg-purple-600", description: "Audits" },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <RefreshCw className="w-8 h-8 text-orange-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      <MasterSideBar 
        visible={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
        onLogout={() => { localStorage.clear(); window.location.href = "/"; }}
      />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <Topbar onSidebarToggle={() => setSidebarOpen(true)} onSearchChange={setSearchQuery} />

        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto">
            
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-black text-gray-800 tracking-tight uppercase">
                  My Uploads
                </h1>
                <p className="text-gray-500 text-sm">Manage and organize your digital records.</p>
              </div>
              <div className="flex gap-2">
                <button onClick={handleRefresh} className="p-3 bg-white border border-gray-100 rounded-xl text-gray-600 hover:bg-gray-50 shadow-sm transition-all">
                  <RefreshCw size={20} />
                </button>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-xl font-black uppercase text-xs tracking-widest shadow-lg shadow-orange-200 hover:bg-orange-700 transition-all"
                >
                  <Upload size={18} />
                  Upload New
                </button>
              </div>
            </div>

            {/* Category Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {categories.map((cat) => (
                <div
                  key={cat.name}
                  onClick={() => setFilterCategory(cat.name)}
                  className={`cursor-pointer p-5 rounded-3xl border transition-all ${filterCategory === cat.name ? 'bg-white border-orange-500 shadow-md ring-1 ring-orange-500' : 'bg-white border-gray-100 shadow-sm hover:border-orange-200'}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className={`w-10 h-10 ${cat.color} rounded-xl flex items-center justify-center text-white shadow-lg`}>
                      <cat.icon size={20} />
                    </div>
                    <span className="text-xl font-black text-gray-800">{cat.count}</span>
                  </div>
                  <h3 className="text-xs font-black text-gray-800 uppercase tracking-wider">{cat.name}</h3>
                </div>
              ))}
            </div>

            {/* Filter Bar */}
            <div className="bg-white p-4 rounded-3xl border border-gray-100 shadow-sm mb-6 flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input 
                  type="text" 
                  placeholder="Filter by name..." 
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-orange-500"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <select 
                className="bg-gray-50 border-none rounded-xl text-xs font-bold uppercase p-2 px-4 focus:ring-2 focus:ring-orange-500"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date">Sort by Date</option>
                <option value="name">Sort by Name</option>
              </select>
            </div>

            {/* Document Table */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/50">
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Document</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Category</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Uploaded</th>
                      <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    <AnimatePresence>
                      {filteredDocuments.map((doc, idx) => (
                        <motion.tr 
                          initial={{ opacity: 0 }} 
                          animate={{ opacity: 1 }} 
                          key={doc._id || idx}
                          className="hover:bg-gray-50/50 transition-colors"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-orange-50 text-orange-600 rounded-lg">
                                <FileText size={18} />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-gray-800">{doc.originalName}</p>
                                <p className="text-[10px] text-gray-400 font-bold uppercase">{doc.fileType}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-gray-100 text-gray-600 text-[10px] font-black uppercase rounded-full">
                              {doc.category || "General"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-[10px] font-bold text-gray-500">
                            {new Date(doc.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <a href={doc.fileUrl} target="_blank" rel="noreferrer" className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all">
                                <Eye size={18} />
                              </a>
                              <a href={doc.fileUrl} download className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all">
                                <Download size={18} />
                              </a>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
              {filteredDocuments.length === 0 && (
                <div className="py-20 text-center">
                  <p className="text-xs font-black text-gray-400 uppercase tracking-widest">No documents matching your search.</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      <UploadDocumentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        staffId={staffId}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
}